import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, AlertCircle } from "lucide-react";
import BtcLogo from "../components/BtcLogo";

/**
 * BTC Email · Batch 2 — email verification confirmation page.
 *
 * On mount the page POSTs the `?token=` from the BTC Portal
 * verification email to `/api/auth/verify-email`.  The server
 * validates the hashed token server-side; the raw token never
 * leaves this request.
 */
export default function VerifyEmail() {
  const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const [params] = useSearchParams();
  const token = (params.get("token") || "").trim();
  const [status, setStatus] = useState("verifying"); // verifying | ok | error

  useEffect(() => {
    if (!token) {
      setStatus("error");
      return;
    }
    (async () => {
      try {
        const r = await fetch(
          `${API}/api/auth/verify-email?token=${encodeURIComponent(token)}`,
          { method: "POST" }
        );
        setStatus(r.ok ? "ok" : "error");
      } catch (err) {
        setStatus("error");
      }
    })();
  }, [API, token]);

  return (
    <div className="min-h-screen bg-[#F8F9FB] flex items-center justify-center px-4">
      <div
        className="bg-white rounded-3xl border border-gray-100 shadow-sm w-full max-w-md p-8 lg:p-10"
        data-testid="verify-email-page"
      >
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <BtcLogo className="h-12 w-auto" />
        </Link>
        <h1 className="text-3xl font-extrabold tracking-tight text-[#0B132B] mb-2">
          Confirm your email
        </h1>
        {status === "verifying" && (
          <p
            className="text-sm text-slate-600"
            data-testid="verify-email-verifying"
          >
            Confirming your email address…
          </p>
        )}
        {status === "ok" && (
          <div
            className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 mt-2"
            role="status"
            data-testid="verify-email-ok"
          >
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-emerald-700 mt-0.5" aria-hidden="true" />
              <div className="text-sm text-emerald-900">
                <div className="font-semibold mb-1">
                  Your email is confirmed.
                </div>
                <p>You can continue to the BTC Portal from the sign-in page.</p>
              </div>
            </div>
            <Link
              to="/login"
              className="mt-6 inline-flex items-center gap-2 btc-btn-primary"
              data-testid="verify-email-login-link"
            >
              Go to sign in
            </Link>
          </div>
        )}
        {status === "error" && (
          <div
            className="rounded-xl border border-slate-200 bg-slate-50 p-5 mt-2"
            role="alert"
            data-testid="verify-email-error"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-slate-500 mt-0.5" aria-hidden="true" />
              <div className="text-sm text-slate-700">
                <div className="font-semibold text-slate-900 mb-1">
                  This confirmation link is invalid or has expired.
                </div>
                <p>
                  You can request a new one from your account settings once you
                  sign in.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
